import { useState } from "react";
import axios from "axios";
import { Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "coenzio_lead_email";

const triggerDownload = (d) => {
  const a = document.createElement("a");
  a.href = d.file;
  a.download = d.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="text-[13px] font-semibold text-ink">{label}</span>
    <input data-testid={testId} {...props} className="mt-1.5 w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-[15px] text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-cobalt-700" />
  </label>
);

export const LeadGate = ({ study, onClose }) => {
  const [form, setForm] = useState({ name: "", email: localStorage.getItem(KEY) || "", company: "" });
  const [state, setState] = useState({ loading: false, error: "", done: false });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "", done: false });
    try {
      await axios.post(`${API}/leads`, { ...form, name: form.name || null, company: form.company || null, case_study: study.id });
      localStorage.setItem(KEY, form.email);
      triggerDownload(study);
      setState({ loading: false, error: "", done: true });
    } catch (err) {
      const msg = err?.response?.status === 422 ? "Please enter a valid work email." : "Something went wrong. Please try again.";
      setState({ loading: false, error: msg, done: false });
    }
  };

  return (
    <Dialog open={!!study} onOpenChange={(o) => { if (!o) { setState({ loading: false, error: "", done: false }); onClose(); } }}>
      <DialogContent data-lenis-prevent data-testid="lead-gate-dialog" className="w-[calc(100%-24px)] max-w-md rounded-[28px] border-ink/10 bg-paper p-7 font-body sm:rounded-[28px] sm:p-8">
        {state.done ? (
          <div data-testid="lead-gate-success">
            <DialogTitle className="font-display text-2xl font-bold text-ink">Your download has started.</DialogTitle>
            <DialogDescription className="mt-3 text-[15px] leading-relaxed text-ink/70">Thanks! If it didn't start, use the button below.</DialogDescription>
            <a href={study?.file} download={study?.name} data-testid="lead-gate-manual-download" className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-[15px] font-semibold text-paper hover:bg-cobalt-700">
              <Download className="h-4 w-4" /> Download again
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="lead-gate-form">
            <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-cobalt-700">{study?.tag}</p>
            <DialogTitle className="font-display text-2xl font-bold leading-tight text-ink">Get the full case study</DialogTitle>
            <DialogDescription className="text-[15px] leading-relaxed text-ink/70">Enter your work email and the PDF will download straight away.</DialogDescription>
            <Field label="Work email *" testId="lead-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" testId="lead-name-input" value={form.name} onChange={set("name")} maxLength={120} />
              <Field label="Company" testId="lead-company-input" value={form.company} onChange={set("company")} maxLength={160} />
            </div>
            {state.error && <p data-testid="lead-gate-error" className="text-[14px] font-medium text-red-600">{state.error}</p>}
            <button type="submit" disabled={state.loading} data-testid="lead-gate-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-[15px] font-semibold text-paper transition-colors hover:bg-cobalt-700 disabled:opacity-60">
              {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </button>
            <p className="text-[12px] text-ink/60">We'll only use your email to share relevant Coenzio insights. No spam.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
